import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { isEmployerUser, refreshUserProfile, StoredUser } from '../services/subscriptionService';
import NotificationBell from './NotificationBell';
import TokenUsageModal from './TokenUsageModal';
import styles from '../styles/Dashboard.module.css';
import headerStyles from '../styles/modules/Header.module.css';
import cardStyles from '../styles/modules/Card.module.css';
import buttonStyles from '../styles/modules/Button.module.css';
import backgroundImage from '../images/background.jpg';

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [user, setUser] = useState<StoredUser | null>(null);
    const [userType, setUserType] = useState<'staff' | 'employer' | 'admin' | null>(null);
    const [pendingEngagementsCount, setPendingEngagementsCount] = useState<number>(0);
    const [pendingRatingsCount, setPendingRatingsCount] = useState<number>(0);
    const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
    const [showSearchStaffTokenModal, setShowSearchStaffTokenModal] = useState(false);

    const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newLanguage = e.target.value;
        i18n.changeLanguage(newLanguage);

        // Update language preference in API if user is logged in
        const token = localStorage.getItem('authToken');
        if (token) {
            try {
                const response = await fetch(getApiUrl('users/profile'), {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ preferredLanguage: newLanguage })
                });

                if (response.ok && user) {
                    const updatedUser = { ...user, preferredLanguage: newLanguage };
                    setUser(updatedUser);
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                }
            } catch (error) {
                console.error('Error updating language:', error);
            }
        }
    };

    useEffect(() => {
        // Check if user is logged in
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('user');

        if (!token || !userData) {
            // Redirect to landing if not logged in
            navigate('/');
            return;
        }

        try {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);

            // Determine user type based on role
            const staffRoles = [
                'barman', 'bartender', 'waiter', 'waitress', 'chef', 
                'sous chef', 'cook', 'dishwasher', 'host/hostess', 
                'sommelier', 'busser', 'kitchen porter', 'line cook', 'pastry chef'
            ];

            const adminRoles = ['admin', 'superadmin'];

            const isAdmin = adminRoles.some(role => 
                parsedUser.role.toLowerCase().includes(role.toLowerCase())
            );

            const isStaff = staffRoles.some(role => 
                parsedUser.role.toLowerCase().includes(role.toLowerCase())
            );

            if (isAdmin) {
                // Redirect admin/superadmin to admin dashboard
                navigate('/admin');
                return;
            }

            if (parsedUser.userType === 'staff' || isStaff) {
                setUserType('staff');
            } else {
                setUserType('employer');
            }
        } catch (error) {
            console.error('Error parsing user data:', error);
            navigate('/');
        }
    }, [navigate]);

    // Fetch pending engagements count
    useEffect(() => {
        const fetchEngagementStats = async () => {
            const token = localStorage.getItem('authToken');
            
            if (!token || !userType) return;

            try {
                const response = await fetch(getApiUrl('engagements/stats'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setPendingEngagementsCount(data.pending || 0);
                }
            } catch (error) {
                console.error('Error fetching engagement stats:', error);
            }
        };

        fetchEngagementStats();
        // Refresh every 30 seconds to check for new engagements
        const interval = setInterval(fetchEngagementStats, 30000);
        return () => clearInterval(interval);
    }, [userType]);

    // Fetch pending ratings count
    useEffect(() => {
        const fetchPendingRatings = async () => {
            const token = localStorage.getItem('authToken');
            
            if (!token || !userType) return;

            try {
                // Fetch all engagements
                const engagementsResponse = await fetch(getApiUrl('engagements/my'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!engagementsResponse.ok) return;

                const engagements = await engagementsResponse.json();

                // Fetch rated engagements
                const ratingsResponse = await fetch(getApiUrl('ratings/my-given?limit=100'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                let ratedEngagementIds = new Set<string>();
                if (ratingsResponse.ok) {
                    const ratingsData = await ratingsResponse.json();
                    ratedEngagementIds = new Set(
                        ratingsData.ratings?.map((r: any) => r.engagement?._id || r.engagementId).filter(Boolean) || []
                    );
                }

                // Count completed engagements that need rating
                const pendingRatings = engagements.filter((e: any) => {
                    if (e.status !== 'completed' || e.completedBy !== 'both') return false;
                    if (ratedEngagementIds.has(e._id)) return false;
                    
                    // Check 30-day window
                    if (e.completedDate) {
                        const completedDate = new Date(e.completedDate);
                        const daysSinceCompletion = Math.floor((Date.now() - completedDate.getTime()) / (1000 * 60 * 60 * 24));
                        return daysSinceCompletion <= 30;
                    }
                    return true;
                });

                setPendingRatingsCount(pendingRatings.length);
            } catch (error) {
                console.error('Error fetching pending ratings:', error);
            }
        };

        fetchPendingRatings();
        // Refresh every 60 seconds
        const interval = setInterval(fetchPendingRatings, 60000);
        return () => clearInterval(interval);
    }, [userType]);

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        navigate('/');
    };

    const handleSearchJobs = () => {
        navigate('/search-job');
    };

    const handleSearchStaff = async () => {
        const token = localStorage.getItem('authToken');
        let effectiveUser = user;

        if (token) {
            try {
                const latestUser = await refreshUserProfile(token);
                setUser(latestUser);
                effectiveUser = latestUser;
            } catch (error) {
                console.error('Error refreshing profile before Search Staff access check:', error);
            }
        }

        if (effectiveUser?.quickSearchAccessGranted) {
            navigate('/search-staff');
            return;
        }

        setShowSearchStaffTokenModal(true);
    };

    const handleSearchStaffTokenUsed = () => {
        setShowSearchStaffTokenModal(false);
        navigate('/search-staff');
    };

    if (!user) {
        return null; // or a loading spinner
    }

    const firstName = user.fullName?.trim().split(/\s+/)[0] || user.fullName;

    return (
        <div className={styles.dashboardPage}>
            <header className={headerStyles.headerDashboard}>
                <h1 className={headerStyles.titleLarge}>{t('dashboard.title')}</h1>
                <div className={headerStyles.headerActions}>
                    <div className={styles.headerActionsDesktop}>
                        <div className={styles.languageSelectorTop}>
                            <select value={i18n.language} onChange={handleLanguageChange}>
                                <option value="en">English</option>
                                <option value="sr">Srpski</option>
                            </select>
                        </div>
                        <NotificationBell />
                        {isEmployerUser(user) && (
                            <button className={`${styles.iconButton} ${styles.settingsButton}`} onClick={() => navigate('/settings?tab=billing')} title={t('settings.title')}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                    <circle cx="12" cy="12" r="3"/>
                                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                </svg>
                            </button>
                        )}
                        <button className={`${styles.iconButton} ${styles.profileButton}`} onClick={() => navigate('/profile')} title={t('dashboard.profile')}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                            </svg>
                        </button>
                        <button className={`${styles.iconButton} ${styles.logoutButton}`} onClick={handleLogout} title={t('dashboard.logout')}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                <polyline points="16 17 21 12 16 7"/>
                                <line x1="21" y1="12" x2="9" y2="12"/>
                            </svg>
                        </button>
                    </div>
                    <div className={styles.headerMobileMenu}>
                        <NotificationBell />
                        <button
                            className={buttonStyles.hamburger}
                            onClick={() => setIsHeaderMenuOpen(!isHeaderMenuOpen)}
                            aria-label="Menu"
                            aria-expanded={isHeaderMenuOpen}
                        >
                            <span className={buttonStyles.hamburgerLine}></span>
                            <span className={buttonStyles.hamburgerLine}></span>
                            <span className={buttonStyles.hamburgerLine}></span>
                        </button>
                    </div>
                    {isHeaderMenuOpen && (
                        <div className={styles.headerDropdownMenu}>
                            <div className={styles.languageSelectorMobile}>
                                <label>{t('dashboard.language')}:</label>
                                <select value={i18n.language} onChange={handleLanguageChange}>
                                    <option value="en">English</option>
                                    <option value="sr">Srpski</option>
                                </select>
                            </div>
                            <button className={styles.menuItem} onClick={() => { navigate('/profile'); setIsHeaderMenuOpen(false); }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                    <circle cx="12" cy="7" r="4"/>
                                </svg>
                                {t('dashboard.profile')}
                            </button>
                            {isEmployerUser(user) && (
                                <button className={styles.menuItem} onClick={() => { navigate('/settings?tab=billing'); setIsHeaderMenuOpen(false); }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="3"/>
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                    </svg>
                                    {t('settings.title') || 'Settings'}
                                </button>
                            )}
                            <button className={`${styles.menuItem} ${styles.logout}`} onClick={() => { handleLogout(); setIsHeaderMenuOpen(false); }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                    <polyline points="16 17 21 12 16 7"/>
                                    <line x1="21" y1="12" x2="9" y2="12"/>
                                </svg>
                                {t('dashboard.logout')}
                            </button>
                        </div>
                    )}
                </div>
            </header>

            <div className={styles.dashboardContainer} style={{ backgroundImage: `url(${backgroundImage})` }}>
                <div className={styles.dashboardContent}>
                    <div className={styles.welcomeCard}>
                        <h2 className={styles.welcomeTitle}>
                            <span>{t('dashboard.welcome')}, </span>
                            <span className={styles.userName}>{firstName}!</span>
                        </h2>
                        <p className={styles.userRole}>({user.role})</p>
                    </div>

                    <div className={cardStyles.cardGlass}>
                        <h3 className={styles.actionsTitle}>{t('dashboard.quickActions')}</h3>

                        {userType === 'admin' && (
                            <button className={`${styles.actionButton} ${styles.actionButtonAdmin}`} onClick={() => navigate('/admin')}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                                    <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
                                </svg>
                                {t('admin.title')}
                            </button>
                        )}

                        {userType === 'staff' && (
                            <>
                                <button className={styles.actionButton} onClick={handleSearchJobs}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"/>
                                        <path d="M21 21l-4.35-4.35"/>
                                    </svg>
                                    {t('dashboard.searchJob')}
                                </button>
                                <button className={`${styles.actionButton} ${styles.actionButtonSecondary}`} onClick={() => navigate('/my-availability')}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                    </svg>
                                    {t('dashboard.myAvailability')}
                                </button>
                                <button className={`${styles.actionButton} ${styles.actionButtonHighlight}`} onClick={() => navigate('/my-engagements')}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 11l3 3L22 4"/>
                                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                                    </svg>
                                    {t('dashboard.myEngagements')}
                                    {pendingEngagementsCount > 0 && (
                                        <span className={styles.notificationBadge}>{pendingEngagementsCount}</span>
                                    )}
                                    {pendingRatingsCount > 0 && (
                                        <span className={styles.starBadge}>⭐ {pendingRatingsCount}</span>
                                    )}
                                </button>
                            </>
                        )}

                        {userType === 'employer' && (
                            <>
                                <button className={styles.actionButton} onClick={handleSearchStaff}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"/>
                                        <path d="M21 21l-4.35-4.35"/>
                                    </svg>
                                    {t('dashboard.searchStaff')}
                                </button>
                                <button className={styles.actionButton} onClick={() => navigate('/my-job-offers')}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14 2 14 8 20 8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                        <polyline points="10 9 9 9 8 9"/>
                                    </svg>
                                    {t('dashboard.myJobOffers')}
                                </button>
                                <button className={`${styles.actionButton} ${styles.actionButtonHighlight}`} onClick={() => navigate('/my-engagements')}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 11l3 3L22 4"/>
                                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                                    </svg>
                                    {t('dashboard.myEngagements')}
                                    {pendingEngagementsCount > 0 && (
                                        <span className={styles.notificationBadge}>{pendingEngagementsCount}</span>
                                    )}
                                    {pendingRatingsCount > 0 && (
                                        <span className={styles.starBadge}>⭐ {pendingRatingsCount}</span>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <TokenUsageModal
                type="quickSearch"
                isOpen={showSearchStaffTokenModal}
                onClose={() => setShowSearchStaffTokenModal(false)}
                onTokenUsed={handleSearchStaffTokenUsed}
            />

        </div>
    );
};

export default Dashboard;
